import React, { useEffect, useRef } from "react";
import { Prompt } from "react-router-dom";
import { useConfirmation } from "../../Context/ConfirmationContext";

const isReloadShortcut = (event) => {
  if (event.key === "F5") return true;
  const modifier = event.ctrlKey || event.metaKey;
  return modifier && (event.key === "r" || event.key === "R");
};

const UnsavedChangesGuard = ({
  when = false,
  title = "Unsaved Changes",
  message = "You have unsaved changes. Are you sure you want to leave without saving?",
  confirmLabel = "Discard & Leave",
  cancelLabel = "Stay",
  confirmColor = "#d32f2f",
  details,
  allowPath,
}) => {
  const confirm = useConfirmation();
  const allowUnloadRef = useRef(false);
  const promptingRef = useRef(false);
  const whenRef = useRef(when);
  const optionsRef = useRef({});

  whenRef.current = when;
  optionsRef.current = {
    title,
    message,
    confirmLabel,
    cancelLabel,
    confirmColor,
    details,
  };

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (!whenRef.current || !isReloadShortcut(event)) return;
      // Stop the browser reload so the custom dialog can be shown instead
      // of the native "Leave site?" prompt. preventDefault must run
      // synchronously — before any await.
      event.preventDefault();
      event.stopPropagation();
      if (promptingRef.current) return;
      promptingRef.current = true;
      try {
        const ok = await confirm(optionsRef.current);
        if (ok) {
          allowUnloadRef.current = true;
          window.location.reload();
        }
      } finally {
        promptingRef.current = false;
      }
    };

    const handleBeforeUnload = (event) => {
      if (!whenRef.current || allowUnloadRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [confirm]);

  return (
    <Prompt
      when={when}
      message={(nextLocation) => {
        if (typeof allowPath === "function" && allowPath(nextLocation)) {
          return true;
        }
        return JSON.stringify({
          title,
          message,
          confirmLabel,
          cancelLabel,
          confirmColor,
          details,
        });
      }}
    />
  );
};

export default UnsavedChangesGuard;
